import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import routes from '@gik/core/routes';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import type { IHomePageProps } from '@gik/landing-pages/pages/HomePage';
import type { ILandingPageProps } from '@gik/landing-pages/pages/LandingPage';
import { LandingPage } from '@gik/landing-pages/pages/LandingPage';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { SWRConfig } from 'swr';

export default function LandingPageTemplateComp({ fallback }: FallbackProps<IHomePageProps>) {
  return withTitle(
    'More Support For Your Non-Profit',
    <SWRConfig value={{ fallback }}>
      <LandingPage {...landingPageProps} url="/organize-donation-drive" />
    </SWRConfig>
  );
}

LandingPageTemplateComp.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

// FIXME: Replace this strategy with i18n library instead
// @ts-ignore
const landingPageProps: ILandingPageProps = {
  heroSection: {
    hero: {
      title: 'More Support For Your Non-Profit',
      content:
        'Show donors exactly how they can help—whether it’s flexible gift cards, volunteer hours, meals, and more—so you get the help you really need, right when you need it.',
    },
    backgroundImage: `${getAssetsUrl()}landing-pages/headers/hero-groceries-mobile@2x.png`,
    backgroundImageMd: `${getAssetsUrl()}landing-pages/headers/hero-groceries-desktop@2x.png`,
    backgroundColor: '#faf7fc',
  },
  featureFocus: {
    feature: {
      title: 'Why Choose Give InKind?',
      icon: `${getAssetsUrl()}landing-pages/pool-gifts-small.png`,
      features: [
        <>
          <span className={'tw-font-bold'}>Flexible Support That Fits Your Nonprofit or Organization:</span> Flexible
          gift cards, volunteer shifts, meal trains, and financial donations—all in one place.
        </>,
        <>
          <span className={'tw-font-bold'}>Effortlessly Pool, Manage, and Distribute Gift Cards:</span> Quickly collect
          digital gift cards, combine contributions, and distribute support exactly where it&apos;s needed most.
        </>,
        <>
          <span className={'tw-font-bold'}>Track Donors and Easily Send Personal Thanks:</span> Keep clear records of
          who’s contributing, and send meaningful thank-yous to build stronger relationships.
        </>,
        <>
          <span className={'tw-font-bold'}>Simplify Volunteer Scheduling and Boost Participation:</span> Manage
          volunteer sign-ups effortlessly, reducing administrative tasks while increasing engagement.
        </>,
        <>
          <span className={'tw-font-bold'}>Keep Everyone Connected with Automated Reminders:</span> Automatic updates
          and reminders ensure supporters stay informed and involved with minimal effort.
        </>,
      ],
    },
  },
  ctaSection: {
    ctas: [
      {
        title: 'Boost Your Fundraising Drives with a Gift Card Train',
        content:
          'Learn how to start a Gift Card Train to energize your next fundraiser. It’s a simple, impactful way to rally support and provide meaningful, flexible help.',
        icon: `${getAssetsUrl()}landing-pages/features/NP-Feature01.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/boost-organizations-fundraising-drives-gift-card-train',
      },
      {
        title: 'How To Set Up a Give InKind Page for Your Giving Drive',
        content:
          'Churches and companies of all kinds use Give InKind to centralize the ongoing needs of members and employees. Bring individuals together and strengthen group culture.',
        icon: `${getAssetsUrl()}landing-pages/features/NP-Feature02.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/set-give-inkind-page-holiday-giving-drive',
      },
      {
        title: 'Want to Learn More?',
        content: `We'd love to speak with you and share more about how you can use Give InKind to maximize your donor activities.`,
        icon: `${getAssetsUrl()}landing-pages/features/NP-Feature03.png`,
        buttonTitle: 'Contact Us',
        buttonLink: routes.contactByMail,
      },
    ],
  },
};
